import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { unwrapResult } from '@reduxjs/toolkit'

import { loadOrderStepUnsubscribeData } from '@/features/domain/order'
import { selectCalendarRange } from '@/features/domain/ui'
import { useIsUnmounted } from '@/hooks'
import { useAppDispatch } from '@/store'

import { OrdStepId } from '../typings'
import { getOrdStepId } from '../utils'

import {
  hasOrderStepNotification,
  setOrderStepUnsubscribeData,
} from '../../../../../orderNotificationAtom'

type LoadingStatus = 'loading' | 'failed' | 'ready'

const refreshRate = 30 * 1000

export const useOrderStepNotificationUnsubscription = (
  extendedOrder: uui.domain.client.rm.ExtendedOrderStep,
  orderDate?: string,
) => {
  const {
    orderStep: { id: orderStepId },
  } = extendedOrder

  const calendarRange = useSelector(selectCalendarRange)
  const dispatch = useAppDispatch()
  const isUnmounted = useIsUnmounted()

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(() => 'loading')

  const waitingForServer = useRef(false)
  const ordStepId = useMemo(() => getOrdStepId(extendedOrder), [extendedOrder])
  const intervalId = useRef<ReturnType<typeof setTimeout> | null>(null)

  const archivedDate = orderDate ? orderDate : calendarRange.minDate

  const fetchData = useCallback(
    async (ordStepId: OrdStepId, orderStepId: string, orderDate?: string) => {
      waitingForServer.current = true

      const request = await dispatch(
        loadOrderStepUnsubscribeData({
          ordStepId,
          orderDate,
        }),
      )

      let hasError = false

      if (loadOrderStepUnsubscribeData.fulfilled.match(request)) {
        const unsubscribeData = unwrapResult(request)

        setOrderStepUnsubscribeData(orderStepId, unsubscribeData)

        waitingForServer.current = false
      } else {
        hasError = true
      }

      if (!isUnmounted()) {
        setLoadingStatus(hasError ? 'failed' : 'ready')
      }
    },
    [dispatch, isUnmounted],
  )

  const autoFetchData = useCallback(() => {
    if (waitingForServer.current || isUnmounted()) return

    fetchData(ordStepId, orderStepId, archivedDate)
  }, [ordStepId, orderStepId, fetchData, archivedDate, isUnmounted])

  useEffect(() => {
    if (hasOrderStepNotification(orderStepId)) {
      setLoadingStatus('ready')
    } else {
      setLoadingStatus('loading')
      fetchData(ordStepId, orderStepId, archivedDate)
    }
  }, [orderStepId, ordStepId, fetchData, archivedDate])

  useEffect(() => {
    intervalId.current = setInterval(autoFetchData, refreshRate)

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current)
      }
    }
  }, [autoFetchData])

  return loadingStatus
}
